import { useEffect, useState, useCallback } from "react";
import { api, fmtDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { ArrowsClockwiseIcon as ArrowsClockwise, CircleIcon as Circle, DesktopIcon as Desktop, WarningCircleIcon as WarningCircle, MagnifyingGlassIcon as MagnifyingGlass } from "@phosphor-icons/react";

export default function NinjaDevices() {
  const [status, setStatus] = useState(null);
  const [orgs, setOrgs] = useState([]);
  const [devices, setDevices] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [q, setQ] = useState("");
  const [fOrg, setFOrg] = useState("all");
  const [fStato, setFStato] = useState("all");
  const [fClass, setFClass] = useState("all");

  const loadStatus = useCallback(() => api.get("/ninjaone/status").then(({ data }) => setStatus(data)).catch(() => {}), []);
  const loadDevices = useCallback(() => {
    const params = {};
    if (q) params.q = q;
    if (fOrg !== "all") params.organization_id = fOrg;
    if (fStato !== "all") params.stato = fStato;
    if (fClass !== "all") params.node_class = fClass;
    api.get("/ninjaone/devices", { params }).then(({ data }) => setDevices(data)).catch(() => setDevices([]));
  }, [q, fOrg, fStato, fClass]);

  useEffect(() => { loadStatus(); api.get("/ninjaone/organizations").then(({ data }) => setOrgs(data)).catch(() => {}); }, [loadStatus]);
  useEffect(() => { loadDevices(); }, [loadDevices]);

  const sync = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/ninjaone/sync");
      if (data.configured === false) { toast.error(data.message); setSyncing(false); return; }
      if (data.running) { toast.info("Sincronizzazione già in corso"); }
      else { toast.success("Sincronizzazione avviata in background…"); }
      pollStatus();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore durante l'avvio della sincronizzazione");
      setSyncing(false);
    }
  };

  const pollStatus = useCallback(() => {
    let tries = 0;
    const iv = setInterval(async () => {
      tries += 1;
      try {
        const { data } = await api.get("/ninjaone/status");
        setStatus(data);
        if (data.status !== "running" || tries > 120) {
          clearInterval(iv);
          setSyncing(false);
          if (data.status === "error") toast.error("Sync fallita: " + (data.error || "errore"));
          else if (data.last_result) toast.success(`Sync completata · ${data.last_result.dispositivi} dispositivi, ${data.last_result.organizzazioni} organizzazioni`);
          loadDevices();
          api.get("/ninjaone/organizations").then(({ data: o }) => setOrgs(o)).catch(() => {});
        }
      } catch { /* continua */ }
    }, 3000);
  }, [loadDevices]);

  useEffect(() => { if (status?.status === "running") { setSyncing(true); pollStatus(); } }, [status?.status]); // eslint-disable-line

  const nodeClasses = [...new Set((devices || []).map((d) => d.node_class).filter(Boolean))];

  if (status && !status.configured) {
    return (
      <Card className="rounded-sm border-border shadow-none p-8 text-center" data-testid="ninja-not-configured">
        <WarningCircle size={36} className="text-amber-500 mx-auto mb-3" />
        <h3 className="font-heading font-semibold mb-1">NinjaOne non configurato</h3>
        <p className="text-sm text-muted-foreground">Imposta Client ID, Client Secret e regione in <b>Impostazioni → Integrazioni API → NinjaOne RMM</b>, poi torna qui per sincronizzare i dispositivi.</p>
      </Card>
    );
  }

  return (
    <div className="space-y-4" data-testid="ninja-devices">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="text-sm text-muted-foreground">
          {status?.device_count != null && <span data-testid="ninja-device-count">{status.device_count} dispositivi sincronizzati</span>}
          {status?.last_sync && <span> · ultima sync {fmtDate(status.last_sync)}</span>}
          {status?.base_url && <span> · {status.base_url}</span>}
        </div>
        <Button onClick={sync} disabled={syncing} className="rounded-sm gap-2" data-testid="ninja-sync-btn">
          <ArrowsClockwise size={16} className={syncing ? "animate-spin" : ""} /> {syncing ? "Sincronizzazione…" : "Sincronizza ora"}
        </Button>
      </div>

      <div className="flex flex-wrap gap-2 items-center">
        <div className="relative">
          <MagnifyingGlass size={15} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca dispositivo / organizzazione…" className="pl-8 rounded-sm bg-white w-72" data-testid="ninja-search" />
        </div>
        <Select value={fOrg} onValueChange={setFOrg}>
          <SelectTrigger className="w-56 rounded-sm bg-white" data-testid="ninja-filter-org"><SelectValue placeholder="Organizzazione" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutte le organizzazioni</SelectItem>
            {orgs.map((o) => <SelectItem key={o.ninja_id} value={String(o.ninja_id)}>{o.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={fStato} onValueChange={setFStato}>
          <SelectTrigger className="w-36 rounded-sm bg-white" data-testid="ninja-filter-stato"><SelectValue placeholder="Stato" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutti gli stati</SelectItem>
            <SelectItem value="online">Online</SelectItem>
            <SelectItem value="offline">Offline</SelectItem>
          </SelectContent>
        </Select>
        {nodeClasses.length > 0 && (
          <Select value={fClass} onValueChange={setFClass}>
            <SelectTrigger className="w-44 rounded-sm bg-white" data-testid="ninja-filter-class"><SelectValue placeholder="Tipo" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti i tipi</SelectItem>
              {nodeClasses.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
        <span className="text-sm text-muted-foreground ml-auto" data-testid="ninja-count">{(devices || []).length} dispositivi</span>
      </div>

      <Card className="rounded-sm border-border shadow-none">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Dispositivo</TableHead>
              <TableHead>Organizzazione</TableHead>
              <TableHead>Tipo</TableHead>
              <TableHead>Sistema operativo</TableHead>
              <TableHead>Ultimo contatto</TableHead>
              <TableHead>Stato</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {devices === null && <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-10">Caricamento…</TableCell></TableRow>}
            {devices?.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-10">Nessun dispositivo. Premi "Sincronizza ora" per importarli da NinjaOne.</TableCell></TableRow>}
            {devices?.map((d) => (
              <TableRow key={d.ninja_id} data-testid="ninja-device-row" className="hover:bg-secondary">
                <TableCell className="font-medium">
                  <div className="flex items-center gap-2"><Desktop size={15} className="text-muted-foreground" /> {d.system_name || d.display_name || d.dns_name || "—"}</div>
                  {d.model && <div className="text-xs text-muted-foreground pl-6">{d.manufacturer} {d.model}</div>}
                </TableCell>
                <TableCell className="text-muted-foreground">{d.organization_name || "—"}</TableCell>
                <TableCell className="text-sm">{d.node_class || "—"}</TableCell>
                <TableCell className="text-sm">{d.os_name || "—"}{d.os_build ? <span className="text-muted-foreground"> · {d.os_build}</span> : ""}</TableCell>
                <TableCell className="tabular-nums text-sm">{d.last_contact ? fmtDate(d.last_contact) : "—"}</TableCell>
                <TableCell>
                  <span className={`inline-flex items-center gap-1.5 text-xs font-medium ${d.offline ? "text-slate-500" : "text-emerald-600"}`} data-testid={`ninja-stato-${d.ninja_id}`}>
                    <Circle size={9} weight="fill" /> {d.offline ? "Offline" : "Online"}
                  </span>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
